import React, { useState, useEffect, useCallback } from 'react';
import { LogOut, Radio, Settings, Filter, MessageSquare, Sparkles, Music, ScrollText, ShoppingBag, BarChart3, CalendarClock, Inbox } from 'lucide-react';
import api from '../services/api';
import ChannelsTab from './tabs/ChannelsTab';
import MessagesTab from './tabs/MessagesTab';
import FiltersTab from './tabs/FiltersTab';
import GeminiTab from './tabs/GeminiTab';
import SettingsTab from './tabs/SettingsTab';
import MusicTab from './tabs/MusicTab';
import LogTab from './tabs/LogTab';
import AliExpressTab from './tabs/AliExpressTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import NotificationsTab from './tabs/NotificationsTab';

const TABS = [
  { id: 'inbox', label: 'תיבת הודעות', icon: Inbox },
  { id: 'channels', label: 'ניהול ערוצים', icon: Radio },
  { id: 'messages', label: 'הודעות', icon: MessageSquare },
  { id: 'logs', label: 'לוג פעילות', icon: ScrollText },
  { id: 'music', label: 'מוזיקה', icon: Music },
  { id: 'aliexpress', label: 'אלי אקספרס', icon: ShoppingBag },
  { id: 'analytics', label: 'אנליטיקה', icon: BarChart3 },
  { id: 'filters', label: 'סינונים', icon: Filter },
  { id: 'gemini', label: 'הגדרות Gemini', icon: Sparkles },
  { id: 'settings', label: 'הגדרות מערכת', icon: Settings }
];

export default function Dashboard({ onLogout }) {
  const [activeTab, setActiveTab] = useState('channels');
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // מספר ההתראות שלא נקראו - לתג על הלשונית, מתעדכן כל דקה
  const handleCounts = useCallback((c) => setUnreadCount(c.unread || 0), []);
  useEffect(() => {
    const fetchSummary = () => api.get('/notifications/summary').then(res => handleCounts(res.data)).catch(() => {});
    fetchSummary();
    const timer = setInterval(fetchSummary, 60000);
    return () => clearInterval(timer);
  }, [handleCounts]);

  useEffect(() => {
    api.get('/settings').then(res => {
      const endDate = res.data?.subscription_end_date;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        const now = new Date();
        const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        setDaysRemaining(diff);
      }
    }).catch(() => {});
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'inbox':
        return <NotificationsTab onNavigate={setActiveTab} onCountsChange={handleCounts} />;
      case 'channels':
        return <ChannelsTab />;
      case 'messages':
        return <MessagesTab />;
      case 'logs':
        return <LogTab />;
      case 'music':
        return <MusicTab />;
      case 'aliexpress':
        return <AliExpressTab />;
      case 'analytics':
        return <AnalyticsTab />;
      case 'filters':
        return <FiltersTab />;
      case 'gemini':
        return <GeminiTab />;
      case 'settings':
        return <SettingsTab />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-2 min-h-[4rem] py-2">
            <div className="flex items-center min-w-0 flex-wrap gap-y-1">
              <div className="flex items-center justify-center w-10 h-10 shrink-0 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                <Radio className="w-6 h-6 text-white" />
              </div>
              <h1 className="mr-3 text-base sm:text-xl font-bold text-gray-900 truncate">
                <span className="sm:hidden">ניהול חדשות</span>
                <span className="hidden sm:inline">מערכת ניהול חדשות - טלגרם</span>
              </h1>
              {daysRemaining !== null && (
                <div className={`mr-3 sm:mr-4 flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${
                  daysRemaining <= 3
                    ? 'bg-red-100 text-red-700'
                    : daysRemaining <= 7
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-green-100 text-green-700'
                }`}>
                  <CalendarClock className="w-4 h-4 ml-2" />
                  {daysRemaining > 0
                    ? `נשארו עוד ${daysRemaining} ימים`
                    : daysRemaining === 0
                    ? 'המנוי מסתיים היום'
                    : 'המנוי פג תוקף'}
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              {daysRemaining !== null && daysRemaining <= 7 && (
                <span className="text-sm text-gray-500 hidden sm:inline">
                  {new Date().toLocaleDateString('he-IL')}
                </span>
              )}
              <button
                onClick={onLogout}
                className="flex items-center shrink-0 px-3 sm:px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4 ml-2" />
                יציאה
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-5 sm:gap-8 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center shrink-0 px-1 py-4 border-b-2 text-sm font-medium transition-colors
                    ${isActive
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="w-5 h-5 ml-2" />
                  {tab.label}
                  {tab.id === 'inbox' && unreadCount > 0 && (
                    <span className="mr-2 min-w-[1.25rem] px-1.5 py-0.5 rounded-full bg-red-600 text-white text-xs leading-none text-center">
                      {unreadCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {renderTabContent()}
      </main>
    </div>
  );
}
